import { Card, CardHeader, DataTable, EmptyState, PageHeader, Skeleton } from "@cafe/ui";
import { useQuery } from "@tanstack/react-query";
import { http, money, STATUS } from "../lib/api";

type Metrics = {
  mrr: Record<string, number>; arr: Record<string, number>; byStatus: Record<string, number>; byPlan: Record<string, number>; newCompanies: number; trialsStarted: number;
  trialToPaid: number; churn: number; overdueInvoices: number; overdueAmount: number; topAddons: { code: string; name: string; n: number }[];
};

function Tile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Card className="flex flex-col gap-1 p-4 sm:p-5">
      <span className="text-sm text-muted">{label}</span>
      <span className="text-2xl font-semibold tabular-nums">{value}</span>
      {hint && <span className="text-xs text-subtle">{hint}</span>}
    </Card>
  );
}

/** Сводка (US-PA-07): доход в месяц/год по валютам, статусы, тарифы, переход с пробного на платный, отток, должники, топ дополнений. */
export default function DashboardPage() {
  const m = useQuery({ queryKey: ["metrics"], queryFn: () => http.get<Metrics>("/dashboard/metrics") });
  const plans = useQuery({ queryKey: ["plans"], queryFn: () => http.get<{ code: string; name: string }[]>("/plans") });
  if (m.isPending) return <Skeleton className="h-96" />;
  if (!m.data) return <EmptyState title="Нет данных" />;
  const d = m.data;
  const cur = Object.keys(d.mrr);
  return (
    <>
      <div><h1 className="text-2xl font-semibold">Сводка</h1><p className="text-sm text-muted">Текущий месяц</p></div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Tile label="Доход в месяц" value={cur.length ? cur.map((c) => money(d.mrr[c]!, c)).join(" · ") : "0"} hint="Сколько платят все клиенты в пересчёте на месяц" />
        <Tile label="Доход в год" value={cur.length ? cur.map((c) => money(d.arr[c]!, c)).join(" · ") : "0"} hint="Доход в месяц × 12" />
        <Tile label="Новых компаний" value={String(d.newCompanies)} hint={`Стали платить после пробного периода: ${Math.round(d.trialToPaid * 100)}%`} />
        <Tile label="Ушли клиенты" value={`${Math.round(d.churn * 100)}%`} hint={`Не оплачено в срок: ${d.overdueInvoices} счетов на ${money(d.overdueAmount)}`} />
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader title="Клиенты по статусу подписки" />
          <ul className="flex flex-col gap-2 px-5 pb-5 text-sm">
            {Object.entries(d.byStatus).map(([k, v]) => <li key={k} className="flex justify-between"><span>{STATUS[k]?.label ?? k}</span><span className="font-semibold tabular-nums">{v}</span></li>)}
          </ul>
        </Card>
        <Card>
          <CardHeader title="Клиенты по тарифам" />
          <ul className="flex flex-col gap-2 px-5 pb-5 text-sm">
            {Object.entries(d.byPlan).map(([k, v]) => <li key={k} className="flex justify-between"><span>{plans.data?.find((p) => p.code === k)?.name ?? k}</span><span className="font-semibold tabular-nums">{v}</span></li>)}
          </ul>
        </Card>
        <Card>
          <CardHeader title="Самые популярные дополнения" />
          <div className="px-2 pb-3">
            <DataTable rows={d.topAddons} rowKey={(a) => a.code} empty={<EmptyState title="Пока нет" />}
              columns={[{ key: "n", header: "Дополнение", cell: (a) => a.name, mobile: "title" }, { key: "c", header: "Компаний", align: "end", cell: (a) => a.n }]} />
          </div>
        </Card>
      </div>
    </>
  );
}
